/**
 * Who can own a row, and which of them are people who log in.
 *
 * The Recruiter filter has three consumers — the Daily Calls Report, the
 * Interested list and the Joining Tracker — and they had drifted: two asked
 * for inactive accounts and one did not, so the same filter offered a
 * different set of names depending on which page you were reading. This is the
 * one list, so that cannot happen again.
 *
 * **Historical accounts are separated so the filter can leave them out.** The
 * importer makes the sheet's Recruiter column authoritative: a name matching
 * no user here gets a deactivated account and owns its rows, so
 * `recruiter_user_id` on thousands of imported calls, submissions and
 * placements points at one of them.
 *
 * Excluding them from the dropdown is the owner's decision, and it has a
 * stated cost worth keeping written down: those rows stay visible with the
 * filter cleared, but there is no way to ask for one imported name's share of
 * the work. The dropdown is a dozen colleagues rather than a dozen colleagues
 * behind seventy-odd spreadsheet names, which is what it was for.
 *
 * Anonymised accounts are dropped before either group is built. They are
 * erased people, their name is the literal string "[anonymised]" for every one
 * of them, and offering that as a filter option is precisely what an erasure
 * exists to prevent. Their rows stay in every list and every count.
 *
 * `historical` is still returned rather than dropped on the floor: it is the
 * set being excluded, and a caller that wants to offer them — a report, an
 * export, a future admin screen — needs the same split rather than its own.
 *
 * They are told apart by their address. `create_historical_user` mints one
 * under a domain RFC 2606 reserves and guarantees will never resolve, which is
 * a convention in one module rather than a column on the table — see the
 * backlog. It is enough to filter a dropdown by and not enough to hang a
 * permission on, which is all that is done with it here.
 *
 * The distinction is worth drawing because "(former)" alone conflated two
 * different things: a colleague who left, whose account was real and whose
 * rows are their own work, and a name that only ever existed because it was a
 * column in a spreadsheet.
 */
import type { Role, User } from '../types';

/** Reserved by RFC 2606. Mirrors HISTORICAL_EMAIL_DOMAIN on the server. */
export const HISTORICAL_EMAIL_DOMAIN = '@historical.invalid';

/**
 * What an erased account's address looks like.
 *
 * Anonymising an employee overwrites the linked login: the name becomes
 * "[anonymised]" and the address becomes `anonymised+<user id>@example.com` --
 * see the erasure route, which explains why example.com rather than a
 * special-use domain. Both halves are matched here rather than just the name,
 * because "[anonymised]" is a display string somebody could conceivably type
 * and the address is minted by one line of code.
 *
 * These are people who asked to be erased. Offering their row as a filter
 * option is the one thing an erasure is supposed to prevent -- the name is
 * gone from the list, but the option would still say "somebody was here, and
 * these are their rows".
 */
const ANONYMISED_PREFIX = 'anonymised+';
const ANONYMISED_DOMAIN = '@example.com';

/**
 * Everyone except employees.
 *
 * A manager or an admin can log a call too, and a filter that offered
 * recruiters alone would hide rows it should show. Employees are the ones who
 * never own recruitment work.
 */
const OWNS_WORK: Role[] = ['super_admin', 'company_admin', 'manager', 'recruiter'];

export interface GroupedRecruiters {
  /** People with a login here, past or present. The filter offers these. */
  current: User[];
  /**
   * Names that exist because a spreadsheet carried them.
   *
   * Excluded from the Recruiter filter. Returned anyway — see the note at the
   * top — because "which are these" is the question, and a second caller
   * should not answer it a second way.
   */
  historical: User[];
}

export function isHistorical(user: User): boolean {
  return (user.email ?? '').toLowerCase().endsWith(HISTORICAL_EMAIL_DOMAIN);
}

/**
 * An account whose person has been erased.
 *
 * Excluded from the filter outright -- not grouped, not suffixed. There is no
 * useful way to offer one: the name is "[anonymised]" for every one of them,
 * so a dropdown listing two would show the same word twice and let somebody
 * filter by a person the system has deliberately forgotten.
 *
 * Their rows are not hidden. A call made by an erased recruiter still appears
 * in the report and still counts; what goes is the ability to ask for them by
 * name, which is what erasure means.
 */
export function isAnonymised(user: User): boolean {
  const email = (user.email ?? '').toLowerCase();
  return email.startsWith(ANONYMISED_PREFIX) && email.endsWith(ANONYMISED_DOMAIN);
}

/**
 * Split the staff list into the people the dropdown offers and the imported
 * names it leaves out.
 *
 * Sorted by name within each group rather than left in id order, because a
 * list of seventy-odd names is scanned rather than read.
 */
export function groupRecruiters(users: User[]): GroupedRecruiters {
  const owners = users.filter(
    (u) => OWNS_WORK.includes(u.role) && !isAnonymised(u),
  );
  const byName = (a: User, b: User) =>
    (a.fullName ?? '').localeCompare(b.fullName ?? '', 'en-IN');

  return {
    current: owners.filter((u) => !isHistorical(u)).sort(byName),
    historical: owners.filter(isHistorical).sort(byName),
  };
}

/**
 * Who a call may be handed to.
 *
 * Narrower than the filter's list, and narrower in two ways that matter. The
 * filter names everyone who *owns* rows, which is the right question when you
 * are asking to see somebody's work; this asks who can be given work, which
 * is a question about the future and excludes accounts nobody can sign in as.
 *
 * Super admins go too. They belong to no company and work no worklist -- the
 * server says the same thing in `owner_is_working_it`, which is why 346,067
 * imported calls sit under one and why `take` exists to get rows out from
 * under it. Offering one as a destination would be a button for making more of
 * exactly that.
 *
 * The server refuses all of this again in `reassign_blocker`. This is so the
 * name is never offered, not so the rule is enforced here.
 */
export function reassignTargets(users: User[]): User[] {
  return groupRecruiters(users).current.filter(
    (u) => u.isActive && u.role !== 'super_admin',
  );
}

/**
 * What to append to a name in the list.
 *
 * Only reaches real accounts now that the imported ones are not offered, but
 * it still checks: every historical account is inactive by construction, and
 * marking one "(former)" would say the wrong thing about a name that was
 * never a colleague.
 */
export function recruiterSuffix(user: User): string {
  if (isHistorical(user)) return '';
  return user.isActive ? '' : ' (former)';
}
